import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';
import { ApiError } from '@/lib/api';

type ToastTone = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  success: (m: string) => void;
  error: (m: string | unknown) => void;
  info: (m: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((tone: ToastTone, message: string) => {
    const id = Date.now() + Math.random();
    setItems((s) => [...s, { id, tone, message }]);
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), tone === 'error' ? 8000 : 4500);
  }, []);
  const api: ToastApi = {
    success: (m) => push('success', m),
    info: (m) => push('info', m),
    error: (m) => push('error', typeof m === 'string' ? m : m instanceof ApiError ? m.message : 'Ein Fehler ist aufgetreten.'),
  };
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed right-4 top-[calc(env(safe-area-inset-top)+0.75rem)] z-[60] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2 sm:bottom-4 sm:top-auto" aria-live="polite">
        {items.map((t) => (
          <div
            key={t.id}
            className={clsx(
              'anim-fade pointer-events-auto flex items-start gap-2 rounded-xl border bg-white p-3 text-sm shadow-lg',
              t.tone === 'success' && 'border-emerald-200',
              t.tone === 'error' && 'border-red-200',
              t.tone === 'info' && 'border-sky-200',
            )}
          >
            {t.tone === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : t.tone === 'error' ? <XCircle className="h-5 w-5 text-red-600" /> : <Info className="h-5 w-5 text-sky-600" />}
            <p className="flex-1 text-slate-700">{t.message}</p>
            <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setItems((s) => s.filter((x) => x.id !== t.id))} aria-label="Schließen">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('ToastProvider fehlt');
  return c;
}
